"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { EASE, spring } from "@/lib/motion";
import { PlatformError } from "@/lib/api/client";
import { messageFor } from "@/lib/api/messages";
import { useProfile } from "@/lib/api/profile";
import { useBalances, useIdentity, useSite } from "@/lib/api/session";
import { fiatRouteOptions, routeOptions, useBankrolls, type RouteOption } from "@/lib/api/transfers";
import { formatAmount } from "@/lib/format/amount";
import { mustVerifyEmailBeforeWithdrawal } from "@/lib/wallet/withdraw";
import {
  browserStorage,
  methodFor,
  modalParamFor,
  presetRoute,
  railFor,
  readRememberedDeposit,
  updateSearchParams,
  writeRememberedDeposit,
  type DepositPreset,
} from "@/lib/wallet/deep-link";
import { AnimatedAmount } from "@/components/ui/AnimatedAmount";
import { useShell } from "@/components/shell/ShellContext";
import { Icon } from "@/components/ui/Icon";
import { useModalChrome } from "@/components/ui/Modal";
import { Button } from "@/components/ui/primitives";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { SectionEmpty, SectionError } from "@/components/ui/states";
import { DepositPanel } from "./DepositPanel";
import { useEmailVerificationRedirect } from "./email-gate";
import { FiatPanel } from "./FiatPanel";
import { Web3WalletProvider } from "./Web3Wallet";
import { WithdrawPanel } from "./WithdrawPanel";

/* The wallet, on real data.

   Balances, bankrolls and transfer routes all come from the platform. Deposit
   addresses are issued per route by `GET /deposit/{routeId}` and withdrawal
   limits by `GET /withdraw/{routeId}` — they are never invented here, because
   an address we made up would take a real deposit to nowhere and a minimum we
   guessed would be refused after the player had typed it.

   Everything in this modal needs a session, so signed out it says so. */

const TABS = ["Deposit", "Withdraw"] as const;
export type WalletTab = (typeof TABS)[number] | "Tip";

export function WalletModal({
  open,
  onClose,
  initialTab = "Deposit",
  preset,
}: {
  open: boolean;
  onClose: () => void;
  initialTab?: WalletTab;
  /* A deposit rail, currency and network a deep link asked for. */
  preset?: DepositPreset;
}) {
  const { openSignIn } = useShell();
  const { isSignedIn, isPending: identityPending } = useIdentity();
  const balances = useBalances(isSignedIn && open);

  const [tab, setTab] = useState<(typeof TABS)[number]>(initialTab === "Withdraw" ? "Withdraw" : "Deposit");

  const bankrolls = useBankrolls(open && isSignedIn);

  useEffect(() => {
    if (open) {
      setTab(initialTab === "Withdraw" ? "Withdraw" : "Deposit");
    }
  }, [open, initialTab]);

  /* The address follows the wallet, as the whitelabel's WalletHubProvider
     keeps it: `?modal=` names the open tab, and closing the wallet — or
     leaving Deposit — drops the deposit preset with it. Only a wallet that
     was open clears anything, so a link that has not been acted on yet is
     left alone. */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      updateSearchParams(
        tab === "Withdraw"
          ? { modal: modalParamFor(tab), depositMethod: null, depositToken: null, depositNetwork: null }
          : { modal: modalParamFor(tab) },
      );
      return;
    }

    if (wasOpen.current) {
      wasOpen.current = false;
      updateSearchParams({ modal: null, depositMethod: null, depositToken: null, depositNetwork: null });
    }
  }, [open, tab]);

  /* The whitelabel's gate on the Withdraw tab (WalletHubProvider +
     mustVerifyEmailBeforeWithdrawal): on a site that releases withdrawals only
     to a verified email, an account with none is sent to add one instead of
     being shown a form the platform will refuse. */
  const site = useSite();
  const profile = useProfile(isSignedIn && open);
  const redirectToEmailVerification = useEmailVerificationRedirect();
  const emailGated =
    open &&
    tab === "Withdraw" &&
    mustVerifyEmailBeforeWithdrawal({ policies: site.data?.policies, profile: profile.data ?? undefined });

  useEffect(() => {
    if (emailGated) redirectToEmailVerification();
  }, [emailGated, redirectToEmailVerification]);

  const panel = useModalChrome(open, onClose);

  const primary = balances.data?.at(0);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />

          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Wallet"
            initial={{ opacity: 0, scale: 0.95, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="modal-shadow scrollbar-none relative flex max-h-[90vh] w-full max-w-[400px] flex-col items-center gap-[12px] overflow-y-auto rounded-2xl border border-border-input bg-elevation-1 p-[24px]"
          >
            <div className="flex w-full items-center gap-[4px]">
              <h2 className="flex flex-1 items-center gap-[4px] text-[15.75px] font-bold leading-[1.6] tracking-normal lg:text-[17.5px]">
                Wallet
                {primary && (
                  <span className="text-brand-bold tabular-nums">
                    <AnimatedAmount amount={primary.amount} token={primary.bankroll.token} /> {primary.bankroll.token.symbol}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close wallet"
                className="grid h-[24px] w-[24px] shrink-0 place-items-center text-fg transition-colors hover:text-fg-muted"
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            {!isSignedIn && !identityPending ? (
              <div className="flex w-full flex-col items-center gap-[12px] py-[40px] text-center">
                <span className="grid h-[38px] w-[38px] place-items-center rounded-full border border-border bg-elevation-2 text-fg">
                  <Icon name="wallet" size={18} />
                </span>
                <p className="text-[12px] font-bold leading-[1.6] text-fg">Your wallet needs an account</p>
                <p className="max-w-[38ch] text-[12px] leading-[1.6] text-fg-subtle">
                  Balances, deposits and withdrawals are all tied to a signed-in player.
                </p>
                <Button
                  onClick={() => {
                    onClose();
                    openSignIn();
                  }}
                >
                  Sign in
                </Button>
              </div>
            ) : (
              <>
                <div className="flex w-full items-center gap-[4px] rounded-lg bg-elevation-2 p-[4px]">
                  {TABS.map((entry) => (
                    <button
                      key={entry}
                      type="button"
                      onClick={() => setTab(entry)}
                      className={cn(
                        "relative h-[30px] flex-1 rounded-md px-[12px] py-[4px] text-[12px] font-bold leading-[1.6] transition-colors",
                        tab === entry ? "text-fg-on-primary" : "text-fg-subtle hover:text-fg",
                      )}
                    >
                      {tab === entry && (
                        <motion.span
                          layoutId="wallet-tab"
                          transition={spring}
                          className="absolute inset-0 rounded-md bg-brand"
                        />
                      )}
                      <span className="relative">{entry}</span>
                    </button>
                  ))}
                </div>

                <Balances balances={balances} />

                {bankrolls.isError && (
                  <SectionError
                    title="Wallet routes didn’t load"
                    body={messageFor(
                      bankrolls.error instanceof PlatformError
                        ? bankrolls.error
                        : new PlatformError(0, "network", ""),
                      "wallet",
                    )}
                  />
                )}

                {bankrolls.data && (
                  <Web3WalletProvider>
                    <Routes bankrolls={bankrolls.data} tab={tab} balances={balances.data ?? []} preset={preset} />
                  </Web3WalletProvider>
                )}              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function Balances({ balances }: { balances: ReturnType<typeof useBalances> }) {
  if (balances.isPending) {
    return <div className="h-[58px] w-full animate-pulse rounded-lg bg-elevation-2" />;
  }

  if (balances.isError) {
    return (
      <SectionError
        title="Balance didn’t load"
        body={messageFor(
          balances.error instanceof PlatformError ? balances.error : new PlatformError(0, "network", ""),
          "wallet",
        )}
      />
    );
  }

  if (!balances.data || balances.data.length === 0) {
    return (
      <div className="w-full rounded-lg border border-border-input bg-elevation-2 px-[12px] py-[8px] text-[12px] leading-[1.6] text-fg-subtle">
        No balance yet. Deposit to get started.
      </div>
    );
  }

  return (
    <ul className="w-full divide-y divide-border overflow-hidden rounded-lg border border-border-input bg-elevation-2">
      {balances.data.map((balance) => (
        <li key={balance.bankroll.id} className="flex h-[42px] items-center justify-between px-[12px] py-[8px]">
          <span className="text-[12px] font-bold leading-[1.6] text-fg">{balance.bankroll.token.symbol}</span>
          <span className="text-[14px] font-bold leading-[1.6] tabular-nums text-fg">
            {formatAmount(balance.amount, balance.bankroll.token)}
          </span>
        </li>
      ))}
    </ul>
  );
}

type Balance = {
  amount: string;
  trancheLockedAmount: string;
  amlLockedAmount: string;
  bankroll: { id: string };
};

function Routes({
  bankrolls,
  tab,
  balances,
  preset,
}: {
  bankrolls: Parameters<typeof routeOptions>[0];
  tab: (typeof TABS)[number];
  balances: Balance[];
  preset?: DepositPreset;
}) {
  const direction = tab === "Deposit" ? "deposit" : "withdraw";
  const options = routeOptions(bankrolls, direction);
  const fiat = fiatRouteOptions(bankrolls, direction);

  /* Where the deposit side starts: a deep link's preset, else the choice the
     player last made in this browser (the whitelabel's DepositContext), else
     the first route. Read once, when the wallet opens. */
  const [start] = useState(() => {
    const remembered = readRememberedDeposit(browserStorage());
    const linked = preset?.method || preset?.token ? preset : undefined;
    const choice = linked ?? remembered;
    const route = direction === "deposit" ? presetRoute(options, { bankrollId: remembered.bankrollId, ...choice }) : undefined;

    return {
      route: route?.route.id ?? null,
      rail: direction === "deposit" && choice.method ? railFor(choice.method) : null,
    };
  });

  const [selected, setSelected] = useState<string | null>(start.route);
  const active = options.find((option) => option.route.id === selected) ?? options.at(0) ?? null;

  /* The kit gives each payment rail its own pill — Crypto / Card Payment / Bank
     Transfer on deposit, Crypto / Bank Transfer on withdraw (there is no fiat
     withdrawal by card). Card and bank share one Stablemint route and differ
     only in which initiate endpoint they hit, so both pills appear whenever a
     fiat route exists. A pill only shows if its rail is configured, so a
     crypto-only site sees no switch at all. */
  const fiatRoute = fiat.at(0) ?? null;
  const rails: { key: "crypto" | "card" | "bank"; label: string }[] = [];
  if (options.length > 0) rails.push({ key: "crypto", label: "Crypto" });
  if (fiatRoute && direction === "deposit") rails.push({ key: "card", label: "Card Payment" });
  if (fiatRoute) rails.push({ key: "bank", label: "Bank Transfer" });

  const [rail, setRail] = useState<"crypto" | "card" | "bank" | null>(start.rail);
  const showing = rails.find((entry) => entry.key === rail)?.key ?? rails.at(0)?.key ?? null;

  /* While on Deposit, the address and this browser both keep the choice:
     `depositMethod`, and on the crypto rail `depositToken` and
     `depositNetwork`, exactly as the whitelabel writes them. */
  const crypto = showing === "crypto" ? active : null;
  const token = crypto?.token.symbol ?? null;
  const network = crypto?.token.network.id ?? null;
  const bankrollId = crypto?.bankroll.id ?? null;

  useEffect(() => {
    if (direction !== "deposit" || !showing) return;

    updateSearchParams({ depositMethod: methodFor(showing), depositToken: token, depositNetwork: network });

    writeRememberedDeposit(browserStorage(), {
      ...readRememberedDeposit(browserStorage()),
      method: methodFor(showing),
      ...(token && network && bankrollId ? { token, network, bankrollId } : {}),
    });
  }, [direction, showing, token, network, bankrollId]);

  if (rails.length === 0) {
    return (
      <SectionEmpty
        icon="wallet"
        title={`No ${direction} routes`}
        body={`This site has no ${direction} route configured, so there is nothing to ${direction} through.`}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-[12px]">
      {rails.length > 1 && (
        <div className="flex gap-[8px]">
          {rails.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setRail(entry.key)}
              aria-pressed={showing === entry.key}
              className={cn(
                "h-[30px] flex-1 rounded-md px-[12px] py-[4px] text-[12px] font-bold leading-[1.6] transition-colors",
                showing === entry.key
                  ? "bg-brand text-fg-on-primary"
                  : "bg-elevation-3 text-fg-subtle hover:text-fg",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>
      )}

      {(showing === "card" || showing === "bank") && fiatRoute && (
        <FiatPanel
          key={`${showing}-${fiatRoute.route.id}`}
          option={fiatRoute}
          direction={direction}
          method={showing}
          balance={balanceFor(balances, fiatRoute)}
        />
      )}

      {showing === "crypto" && options.length > 1 && (
        <CurrencyNetworkPickers options={options} active={active} onSelect={setSelected} />
      )}

      {showing === "crypto" && active && direction === "deposit" && (
        <DepositPanel key={active.route.id} option={active} />
      )}
      {showing === "crypto" && active && direction === "withdraw" && (
        <WithdrawPanel
          key={active.route.id}
          option={active}
          balance={balanceFor(balances, active)}
          locks={locksFor(balances, active)}
        />
      )}
    </div>
  );
}

/* The kit's Currency and Network dropdowns, replacing one pill per route.

   The live site runs twenty-plus token-network pairs; as pills they filled the
   whole modal, which is exactly why the kit picks the pair in two steps —
   `modal-deposit-usdc` shows "Currency: USD Coin USDC" over "Network:
   Ethereum ERC-20". Choosing a currency moves to its first network; choosing a
   network picks the exact route. */
function CurrencyNetworkPickers({
  options,
  active,
  onSelect,
}: {
  options: RouteOption[];
  active: RouteOption | null;
  onSelect: (routeId: string) => void;
}) {
  const bySymbol = new Map<string, RouteOption[]>();

  for (const option of options) {
    const list = bySymbol.get(option.token.symbol) ?? [];
    list.push(option);
    bySymbol.set(option.token.symbol, list);
  }

  const activeSymbol = active?.token.symbol ?? options[0]?.token.symbol;
  const networks = bySymbol.get(activeSymbol) ?? [];

  return (
    <div className="flex w-full flex-col gap-[12px]">
      <Picker
        label="Currency"
        value={
          active && (
            <span className="flex items-center gap-[8px]">
              <TokenLogo token={active.token} />
              <span className="font-bold text-fg">{active.token.name}</span>
              <span className="text-fg-subtle">{active.token.symbol}</span>
            </span>
          )
        }
      >
        {(close) =>
          [...bySymbol.entries()].map(([symbol, list]) => (
            <PickerRow
              key={symbol}
              selected={symbol === activeSymbol}
              onClick={() => {
                onSelect(list[0].route.id);
                close();
              }}
            >
              <TokenLogo token={list[0].token} />
              <span className="font-bold text-fg">{list[0].token.name}</span>
              <span className="text-fg-subtle">{symbol}</span>
            </PickerRow>
          ))
        }
      </Picker>

      <Picker
        label="Network"
        value={active && <span className="font-bold text-fg">{active.token.network.name}</span>}
      >
        {(close) =>
          networks.map((option) => (
            <PickerRow
              key={option.route.id}
              selected={option.route.id === active?.route.id}
              onClick={() => {
                onSelect(option.route.id);
                close();
              }}
            >
              <span className="font-bold text-fg">{option.token.network.name}</span>
            </PickerRow>
          ))
        }
      </Picker>
    </div>
  );
}

function TokenLogo({ token }: { token: RouteOption["token"] }) {
  if (!token.logoUrl) {
    return (
      <span className="grid h-[20px] w-[20px] shrink-0 place-items-center rounded-full bg-elevation-3 text-fg-subtle">
        <Icon name="coins" size={12} />
      </span>
    );
  }

  return (
    <span className="relative h-[20px] w-[20px] shrink-0 overflow-hidden rounded-full">
      <RemoteImage src={token.logoUrl} alt="" width={20} height={20} />
    </span>
  );
}

/* A field-shaped disclosure: the kit draws these as 42px fields with a chevron,
   opening a list of the same rows. Closes on escape, on outside click, and on
   pick. Options render through a function so each row can close the list. */
function Picker({
  label,
  value,
  children,
}: {
  label: string;
  value: ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const away = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);

    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative w-full">
      <p className="mb-[4px] text-[12px] font-bold leading-[1.6] text-fg-subtle">{label}</p>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex h-[42px] w-full items-center justify-between gap-[8px] rounded-lg border border-border-input bg-elevation-2 px-[12px] text-[12px] leading-[1.6] transition-colors hover:border-brand"
      >
        {value}
        <Icon
          name="chevron-down"
          size={16}
          className={cn("shrink-0 text-fg-subtle transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute inset-x-0 top-full z-20 mt-[4px] max-h-[240px] overflow-y-auto rounded-lg border border-border-input bg-elevation-2 py-[4px] shadow-lg"
        >
          {children(() => setOpen(false))}
        </ul>
      )}
    </div>
  );
}

function PickerRow({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex h-[38px] w-full items-center gap-[8px] px-[12px] text-left text-[12px] leading-[1.6] transition-colors hover:bg-elevation-3",
          selected && "bg-elevation-3",
        )}
      >
        {children}
      </button>
    </li>
  );
}

/* A route pays out of its own bankroll, so the ceiling on a withdrawal is that
   bankroll's balance and not the sum on screen. No balance row means nothing
   has ever landed there, which is a zero rather than a missing value. */
const balanceFor = (balances: Balance[], option: RouteOption) =>
  balances.find((balance) => balance.bankroll.id === option.bankroll.id)?.amount ?? "0";

const locksFor = (balances: Balance[], option: RouteOption) => {
  const balance = balances.find((entry) => entry.bankroll.id === option.bankroll.id);

  return {
    trancheLockedAmount: balance?.trancheLockedAmount ?? "0",
    amlLockedAmount: balance?.amlLockedAmount ?? "0",
  };
};
